import React, { useEffect, useState } from 'react';
import { Outlet, useOutletContext, useLocation, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import RootHeader from '@components/header/RootHeader';
import Sidebar from '@components/sidebar/Sidebar';
// import { getMyIp } from '@api/other';
// import { getDevice } from '@api/user';
import { STATUS_CODE } from '@constants/api';
import { getDVCInLS, setDVCInLS } from '@utils/ls';
import { IS_NEW_AW, isShowInstallFloatButton, shouldDisplayBalanceError, shouldDisplayLoginButton, shouldPromptPWAInstall, IS_EMBEDDED_APP, getEmbeddedTopOffset } from '@constants/config';
import useMaintenance from '@hooks/useMaintenance';
// import MarqueeView from '@components/shared/MarqueeView';
import LoginModal from '@components/shared/modals/LoginModal';
import SharedLayout1Modal from '@components/shared/modals/SharedLayout1Modal';
import SharedLayout2Modal from '@components/shared/modals/SharedLayout2Modal';

import { getUserInfo } from '@api/user';
import { useGlobalProps } from '@hooks/useGlobalProps';
import usePermissionPages from '@hooks/usePermissionPages';
import LoggedOutModal from '@components/shared/modals/LoggedOutModal';
import AlertModal from '@components/shared/modals/AlertModal';
import { SWITCH_ROUTES } from '@constants/routes';
import PullToRefresh from 'react-simple-pull-to-refresh';
import MetaPixel from '@utils/metaPixel';
import AddToHomeFloatButton from '@components/shared/AddToHomeFloatButton';
import useMobileDetect from '@hooks/useMobileDetect';
import { isPWAInstalled } from '@utils/pwaUtils';


export default function Root() {
    const [openSideBarModal, setOpenSideBarModal] = useState(false);
    const [openLoginModal, setOpenLoginModal] = useState(false);
    const [openAlertModal, setOpenAlertModal] = useState(false);
    // App 內嵌時的頂部留白（px）。需在 DOM 就緒後量測 safe-area，故走 state。
    const [embeddedTopOffset, setEmbeddedTopOffset] = useState(0);

    const { user, balanceError, shareDialogLayout, defaultLanguage, getBalance, pixelCodes, isUserLoading } = useGlobalProps();

    const { t, i18n, ready } = useTranslation();
    const tError = t('error', { returnObjects: true });
    const location = useLocation();  // Use the location hook
    const isMobileBrowser = useMobileDetect();

    useMaintenance();
    usePermissionPages();
    
    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };
    const onToggleSideBarModal = () => {
        setOpenSideBarModal((prev) => !prev);
    };
    const onToggleLoginModal = () => {
        setOpenLoginModal((prev) => !prev);
    };
    const init = async () => {
        const dvc = getDVCInLS();
        if (!dvc) {
            const res = await getUserInfo();
            if (res.rtn === STATUS_CODE.SUCCESS && res.device_id)
                setDVCInLS(res.device_id);
        }
    };


    const handleRefresh = async () => {
        if (user?.ukey)
            await getBalance();
    };

    useEffect(() => {
        init();
        i18n.changeLanguage(defaultLanguage)
    }, [i18n, defaultLanguage]);

    // 量測 App 內嵌所需的頂部留白。轉向時安全區會變，需重新量測。
    useEffect(() => {
        if (!IS_EMBEDDED_APP) return;
        const measure = () => setEmbeddedTopOffset(getEmbeddedTopOffset());
        measure();
        window.addEventListener('resize', measure);
        window.addEventListener('orientationchange', measure);
        return () => {
            window.removeEventListener('resize', measure);
            window.removeEventListener('orientationchange', measure);
        };
    }, []);

    useEffect(() => {
        if (shouldDisplayBalanceError() && balanceError != null)
            setOpenAlertModal(true)
    }, [balanceError]);

    const isLoggedIn = !!user?.ukey;
    
    // Wait for user info to be fetched before checking login status
    if (isUserLoading) {
        return (
            <LoadingWrapper>
                <Spin size="large" />
            </LoadingWrapper>
        );
    }

    // Debug mobile detection
    if (process.env.NODE_ENV === 'development') {
        console.log('Mobile Detection Debug:', {
            isMobileBrowser,
            userAgent: navigator.userAgent,
            innerWidth: window.innerWidth,
            touchSupport: 'ontouchstart' in window,
            maxTouchPoints: navigator.maxTouchPoints
        });
    }
    

    if ((IS_NEW_AW) && shouldPromptPWAInstall() && isMobileBrowser && !isPWAInstalled() && isLoggedIn && (location.pathname !== SWITCH_ROUTES.PWA_INSTALL && location.pathname !== SWITCH_ROUTES.LOGIN)) {
        // 手機瀏覽器 + 已登入 + 未安裝PWA → 強制導向安裝頁
        return <Navigate to={SWITCH_ROUTES.PWA_INSTALL} replace />;
    }

    if (!ready || !i18n) return null;

    const isLogin = !!user?.ukey;
    const isHomeOrRB = location.pathname === '/' || location.pathname === '/rb'; // Check path

    return (
        <>
            {/* <MarqueeView /> */}



            <RootWrap $topOffset={embeddedTopOffset}>
                {pixelCodes && pixelCodes.map((e) => <MetaPixel code={e} />)}

                <Sidebar
                    openSideBarModal={openSideBarModal}
                    onToggleSideBarModal={onToggleSideBarModal}
                    setOpenLoginModal={setOpenLoginModal}
                    isLogin={isLogin}
                />
                <PullToRefresh className='pull-to-refresh' onRefresh={handleRefresh}>
                    <Detail>
                        <RootHeader
                            onToggleSideBarModal={onToggleSideBarModal}
                            onToggleLoginModal={onToggleLoginModal}
                            changeLanguage={changeLanguage}
                            selectedLang={i18n.language}
                            isLogin={isLogin}
                            acc={user?.acc}
                        />

                        <OutletWrap>
                            <Outlet context={{ t }} />
                        </OutletWrap>

                    </Detail>
                </PullToRefresh>

            </RootWrap >


            {isHomeOrRB && isShowInstallFloatButton && <AddToHomeFloatButton />}

            {shouldDisplayLoginButton() && openLoginModal && (
                <LoginModal onToggleLoginModal={onToggleLoginModal} />
            )
            }
            {shareDialogLayout === 1 && <SharedLayout1Modal />}
            {shareDialogLayout === 2 && <SharedLayout2Modal />}
            <LoggedOutModal />
            <AlertModal
                show={openAlertModal}
                title={tError['error@balance_error']}
                message={balanceError?.msg}
                onConfirm={() => {
                    console.log(' confirm ')
                    setOpenAlertModal(false);
                }}
            />
        </>
    );
}

export function useBOContext() {
    return useOutletContext();
}

const RootWrap = styled.div`
    position: relative;
    display: flex;
    width: 100%;
    height: -webkit-fill-available;
    overflow: hidden;
    ${({ $topOffset }) =>
        $topOffset > 0 &&
        `
        padding-top: ${$topOffset}px;
        box-sizing: border-box;
    `}

    .pull-to-refresh {
        z-index: 11;
    }
`;
const Detail = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow-y: hidden;
`;

const OutletWrap = styled.div`
    padding: 0px 10px 10px 10px;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    overflow-y: auto;
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    width: 100%;
`;
